import React, { memo, useCallback, useEffect, useState } from 'react';
import {
  APILoadingStatus,
  Map,
  MapCameraChangedEvent,
  MapCameraProps,
  MapControl,
  useApiLoadingStatus,
  useMap,
} from '@vis.gl/react-google-maps';
import { useKeycloakUserState } from '@src/shared/context/keycloak-user';
import { IUser } from '@src/shared/context/current-user';
import DeliveryDashboardMapFunctions from '../DeliveryDashboardMapFunctions';
import PIEText from '@src/shared/components/PIEText';
import useDeviceDetect from '@src/shared/hooks/use-device-detect';
import DeliveryDashboardMapMarkers from '../DeliveryDashboardMapMarkers';
import { globalStyles } from 'snacks-design-system';
import DashboardScreenRouter from '../DashboardScreenRouter';
import { useShallow } from 'zustand/react/shallow';
import { INITIAL_ZOOM_LEVEL } from '../../models/model-constants';
import { useDashboardMapStore } from '../../store/dashboardMapStore';
import DashboardCourierDirections from '../DashboardCourierDirections';
import { useDashboardUIStore } from '../../store/dashboardUIStore';
import useDeliveryDashboadAnalytics from '../../tracking/use-delivery-dashboard-analytics';
import { useDebouncedCallback } from 'use-debounce';
const { xdsFontSize16 } = globalStyles;

export const DeliveryDashboardMap = () => {
  const { restaurant } = useKeycloakUserState();
  const INITIAL_CAMERA: MapCameraProps = {
    center: (restaurant as IUser)?.location,
    zoom: INITIAL_ZOOM_LEVEL,
  };

  const { activeCourierId, activeOrderHash } = useDashboardUIStore();
  const status = useApiLoadingStatus();
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const map = useMap();
  const [mapBounds, setMapBounds] = useState<any>(null);
  const [mapCurrentZoomLevel, setMapCurrentZoomLevel] = useState<any>(null);
  const { isResolutionMobile } = useDeviceDetect();
  const [
    mapCenter,
    setMapCenter,
    isMapReFocused,
    setMapReFocusToCenter,
    mapPanOffset,
    mapZoomLevel,
    resetZoomLevel,
  ] = useDashboardMapStore(
    useShallow((state) => [
      state.mapCenter,
      state.setMapCenter,
      state.isMapReFocused,
      state.setMapReFocusToCenter,
      state.mapPanOffset,
      state.mapZoomLevel,
      state.resetZoomLevel,
    ])
  );
  const { trackMapInteraction } = useDeliveryDashboadAnalytics();

  /* MAIN CONTROL OVER REFOCUS MAP */
  useEffect(() => {
    if (isMapReFocused) {
      map?.setCenter(INITIAL_CAMERA.center);
      resetZoomLevel();
      map?.setZoom(INITIAL_CAMERA.zoom);
    }
  }, [isMapReFocused]);

  const handleCameraChange = useCallback(() => {
    setMapReFocusToCenter(false);
    setMapCenter(null);
  }, []);

  const handleZoomChange = useCallback(() => {
    resetZoomLevel();
  }, [mapZoomLevel]);

  useEffect(() => {
    if (mapPanOffset > 0) {
      return map?.panBy(0, mapPanOffset);
    }
  }, [mapCenter]);

  useEffect(() => {
    if (status === APILoadingStatus.LOADING) {
      return;
    }
    if (status === APILoadingStatus.LOADED) {
      setIsMapLoaded(true);
      return;
    }
    if (status === APILoadingStatus.AUTH_FAILURE) {
      console.log('GOOGLE MAP AUTHENTICATION FAILED');
      return;
    }
    if (status === APILoadingStatus.FAILED) {
      console.log('GOOGLE MAP FAILED TO LOAD');
      return;
    }
    if (status === APILoadingStatus.NOT_LOADED) {
      console.log('MAP NOT LAODED');
      return;
    }
  }, [status]);

  const handleMapChange = () => {
    const bounds = map?.getBounds();
    if (map && bounds) {
      const newBounds = bounds.toJSON();
      setMapBounds([newBounds.west, newBounds.south, newBounds.east, newBounds.north]);
    }
  };

  const mapZoomchangeHandler = useDebouncedCallback((e: MapCameraChangedEvent) => {
    handleZoomChange();
    handleMapChange();
    setMapCurrentZoomLevel(e?.detail.zoom);
  }, 250);

  return (
    <>
      {isMapLoaded ? (
        <>
          <Map
            gestureHandling={'greedy'}
            disableDefaultUI={true}
            defaultCenter={INITIAL_CAMERA.center}
            center={mapCenter}
            zoom={mapZoomLevel}
            zoomControl={true}
            zoomControlOptions={{ position: isResolutionMobile ? 4 : 0 }}
            defaultZoom={INITIAL_CAMERA.zoom}
            reuseMaps={true}
            onCameraChanged={handleCameraChange}
            onDragend={() => {
              handleMapChange();
            }}
            onZoomChanged={mapZoomchangeHandler}
            style={{ width: '100%', height: '100%' }}
            mapId={'6efd7302105c583d'}
          >
            {!isResolutionMobile ? (
              <MapControl position={3}>
                <DeliveryDashboardMapFunctions
                  iconPosition="16px"
                  onCenterClick={() => {
                    trackMapInteraction();
                    setMapReFocusToCenter(true);
                  }}
                />
              </MapControl>
            ) : null}
            <DashboardCourierDirections
              courierId={!activeOrderHash && activeCourierId ? activeCourierId : null}
            />
          </Map>

          <DashboardScreenRouter />
          <DeliveryDashboardMapMarkers
            mapInstance={map}
            defaultMapMarkerPosition={INITIAL_CAMERA.center}
            bounds={mapBounds}
            zoom={mapCurrentZoomLevel}
          />
        </>
      ) : (
        <PIEText text="Loading...." size={xdsFontSize16} align="center"></PIEText>
      )}
    </>
  );
};

export default memo(DeliveryDashboardMap);
